<template>
  <div v-if="show" class="slip-dock" v-on:keydown.esc="collapse">
    <!-- Desktop: panel on the right, or a tab when collapsed -->
    <template v-if="desktop">
      <aside
        v-if="open"
        class="fixed right-3 top-[60px] bottom-3 z-50 w-[360px] flex flex-col bg-white/80 backdrop-blur-lg border border-white/40 rounded-xl overflow-hidden shadow-2xl"
        aria-label="Your slip"
      >
        <SlipHeader :count="legs.length" :locked="locked" @collapse="collapse" @clear="clear" />
        <SlipBody :scroll="tall" class="flex-1 min-h-0" :class="{ 'overflow-y-auto': !tall }" />
      </aside>
      <button
        v-else
        type="button"
        class="collapsed-tab fixed right-0 top-1/2 z-50 transform -translate-y-1/2 flex flex-col items-center gap-2 bg-blue-700 text-white rounded-l-xl shadow-xl px-2 py-3 uppercase text-xs font-bold"
        :class="{ pulse: bump }"
        aria-label="Expand slip"
        v-on:click="expand"
      >
        <span aria-hidden="true">‹</span>
        <span class="bg-white text-blue-700 rounded-full px-2 tabular-nums">{{ legs.length }}</span>
        <SlipIcon />
        <span v-if="fairText" class="vertical font-normal normal-case tabular-nums">fair {{ fairText }}</span>
      </button>
    </template>

    <!-- Mobile: bottom bar, peek or full sheet -->
    <template v-else>
      <section
        v-if="open"
        class="sheet fixed left-0 right-0 bottom-0 z-50 flex flex-col bg-white/80 backdrop-blur-lg border-t border-white/20 rounded-t-xl shadow-2xl"
        :style="{ height: open === 'peek' ? '45vh' : '78vh' }"
        aria-label="Your slip"
      >
        <button
          type="button"
          class="mx-auto mt-2 mb-1 w-12 h-4 flex items-center justify-center"
          :aria-label="open === 'peek' ? 'Show the whole slip' : 'Collapse slip'"
          v-on:click="open === 'peek' ? expand() : collapse()"
        >
          <span class="block w-10 h-1 rounded-full bg-gray-400"></span>
        </button>
        <SlipHeader :count="legs.length" :locked="locked" mobile @collapse="collapse" @clear="clear" />
        <SlipBody class="flex-1 min-h-0 overflow-y-auto" />
      </section>
      <button
        v-else
        type="button"
        class="mobile-bar fixed left-0 right-0 bottom-0 z-50 flex items-center justify-between gap-3 bg-blue-700 text-white rounded-t-xl shadow-xl px-4 uppercase text-xs font-bold"
        :class="{ pulse: bump }"
        aria-label="Expand slip"
        v-on:click="expand"
      >
        <span class="flex items-center gap-2"
          ><SlipIcon /> Slip
          <span class="bg-white text-blue-700 rounded-full px-2 tabular-nums">{{ legs.length }}</span></span
        >
        <span v-if="fairText" class="font-normal normal-case tabular-nums">fair {{ fairText }}</span>
        <span aria-hidden="true" class="text-base">⌃</span>
      </button>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import SlipBody from "./SlipBody.vue";
import { formatOdds } from "../../odds";

const SlipIcon = {
  name: "SlipIcon",
  render(h) {
    return h(
      "svg",
      {
        attrs: {
          width: 14, height: 14, viewBox: "0 0 24 24", fill: "none",
          stroke: "currentColor", "stroke-width": 2, "aria-hidden": "true",
        },
      },
      [h("path", { attrs: { d: "M6 3h12v18l-3-2-3 2-3-2-3 2z" } }), h("path", { attrs: { d: "M9 8h6M9 12h6" } })],
    );
  },
};

const SlipHeader = {
  name: "SlipHeader",
  props: { count: Number, locked: Boolean, mobile: Boolean },
  render(h) {
    const link = (label, on, attrs = {}) =>
      h("button", { class: "text-blue-700 uppercase underline", attrs: { type: "button", ...attrs }, on }, label);
    return h("div", { class: "flex items-center justify-between px-4 py-3 border-b border-gray-200" }, [
      h("div", { class: "text-sm font-semibold uppercase text-gray-800" }, [
        "Your slip ",
        h("span", { class: "text-brandGrey font-normal" }, `· ${this.count} ${this.count === 1 ? "leg" : "legs"}`),
      ]),
      h("div", { class: "flex items-center gap-4 text-xs" }, [
        this.count ? link("clear", { click: () => this.$emit("clear") }) : null,
        h(
          "router-link",
          { props: { to: "/slip" }, class: "text-blue-700", attrs: { "aria-label": "Open slip on its own page", title: "Open full page" } },
          "⤢",
        ),
        h(
          "button",
          {
            class: "text-blue-700 uppercase flex items-center gap-1",
            attrs: { type: "button", "aria-label": "Collapse slip" },
            on: { click: () => this.$emit("collapse") },
          },
          ["collapse ", h("span", { class: "text-base leading-none", attrs: { "aria-hidden": "true" } }, this.mobile ? "⌄" : "›")],
        ),
      ]),
    ]);
  },
};

export default {
  name: "SlipDock",
  components: { SlipBody, SlipIcon, SlipHeader },
  data() {
    return { desktop: true, tall: true, bump: false, media: null };
  },
  computed: {
    ...mapState("betslip", ["legs", "open", "priced", "justAdded"]),
    ...mapGetters("betslip", ["locked", "visible"]),
    show() {
      return !!this.$store.getters.subscriptionType && this.visible;
    },
    fairText() {
      const totals = this.priced && this.priced.totals;
      if (this.locked || !totals || !totals.fair_odds) return "";
      return formatOdds(totals.fair_odds, this.$store.getters.preferences.odds_format);
    },
  },
  watch: {
    justAdded(id) {
      if (id && !this.open) {
        this.bump = true;
        setTimeout(() => (this.bump = false), 1600);
      }
    },
    open() {
      this.$emit("open", !!this.open);
    },
  },
  mounted() {
    if (window.matchMedia) {
      this.media = window.matchMedia("(min-width: 1024px)");
      this.desktop = this.media.matches;
      this.onMedia = (e) => (this.desktop = e.matches);
      if (this.media.addEventListener) this.media.addEventListener("change", this.onMedia);
    }
    // pin the totals under a scrolling leg list only when the window is tall enough
    this.onResize = () => (this.tall = window.innerHeight >= 760);
    this.onResize();
    window.addEventListener("resize", this.onResize);
    this.onKey = (e) => {
      if (e.key === "Escape" && this.open) this.collapse();
    };
    window.addEventListener("keydown", this.onKey);
    if (this.legs.length) this.$store.dispatch("betslip/reprice");
  },
  beforeDestroy() {
    if (this.media && this.media.removeEventListener) this.media.removeEventListener("change", this.onMedia);
    window.removeEventListener("keydown", this.onKey);
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    expand() {
      this.$store.dispatch("betslip/open", true);
    },
    collapse() {
      this.$store.dispatch("betslip/open", false);
    },
    clear() {
      this.$store.dispatch("betslip/clear");
    },
  },
};
</script>

<style scoped>
.vertical {
  writing-mode: vertical-rl;
  transform: rotate(180deg);
}
.mobile-bar {
  height: calc(56px + env(safe-area-inset-bottom, 0px));
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
.sheet {
  padding-bottom: env(safe-area-inset-bottom, 0px);
}
@media (prefers-reduced-motion: no-preference) {
  .pulse {
    animation: slip-pulse 1.6s ease-in-out 1;
  }
}
@keyframes slip-pulse {
  0%,
  100% {
    filter: none;
  }
  40% {
    filter: brightness(1.25);
  }
}
</style>
